import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Title } from '@tremor/react';
import { CustomerNotFoundError, fetchCustomer } from '../api/customer';
import type { CustomerBundle } from '../api/types';
import { t } from '../lib/i18n';
import CustomerActivitySection from './CustomerActivitySection';
import CustomerArSection from './CustomerArSection';
import CustomerHeader from './CustomerHeader';
import { useDrilldown } from './DrilldownProvider';
import EmptyState from './EmptyState';
import ErrorRetry from './ErrorRetry';
import IconButton from './IconButton';
import Modal from './Modal';
import TableSkeleton from './TableSkeleton';
import { BackIcon, CloseIcon } from './icons';

interface Props {
  anum: number;
  onClose: () => void;
  /** Supplied by DrilldownHost when this modal sits on top of another. */
  onBack?: () => void;
}

/**
 * Account summary in place of a route change (JCD-59).
 *
 * Composes the same three sections the /customers/:anum page uses rather than
 * duplicating their markup. The scorecard and the full account statement stay
 * on that page — this answers "who is this account and what do they owe"
 * without losing the table behind it.
 */
export default function AccountDetailModal({ anum, onClose, onBack }: Props) {
  // undefined = loading, null = no such account.
  const [bundle, setBundle] = useState<CustomerBundle | null | undefined>(undefined);
  const [err, setErr] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const { open } = useDrilldown();

  useEffect(() => {
    let alive = true;
    setErr(null);
    setBundle(undefined);
    fetchCustomer(anum)
      .then((b) => { if (alive) setBundle(b); })
      .catch((e: Error) => {
        if (!alive) return;
        if (e instanceof CustomerNotFoundError) setBundle(null);
        else setErr(e.message);
      });
    return () => { alive = false; };
  }, [anum, retry]);

  return (
    <Modal onClose={onClose} ariaLabel={t.drilldown.accountTitle} size="4xl" panelClassName="p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-2 min-w-0">
          {onBack && (
            <IconButton onClick={onBack} label={t.drilldown.back} size="sm">
              <BackIcon />
            </IconButton>
          )}
          <Title className="truncate">{t.drilldown.accountTitle} #{anum}</Title>
        </div>
        <IconButton onClick={onClose} label={t.drilldown.close}>
          <CloseIcon />
        </IconButton>
      </div>

      {bundle === undefined && !err && <TableSkeleton rows={6} cols={3} />}

      {err && <ErrorRetry message={err} onRetry={() => setRetry((n) => n + 1)} />}

      {!err && bundle === null && <EmptyState message={t.drilldown.accountNotFound} />}

      {!err && bundle && (
        <div className="space-y-4">
          <CustomerHeader header={bundle.header} />
          <CustomerArSection
            ar={bundle.ar}
            openInvoices={bundle.open_invoices}
            recentPayments={bundle.recent_payments}
            dsoDays={bundle.dso_days}
          />
          <CustomerActivitySection
            recentTickets={bundle.recent_tickets}
            topParts={bundle.top_parts}
            revenueTrend={bundle.revenue_trend}
            // Stays in the stack: account -> ticket -> part, with a back arrow
            // at each level, instead of dropping the user on another page.
            onTicketClick={(sid, tnum) => open({ kind: 'tkt', sid, tnum })}
          />
          <div className="pt-1">
            <Link
              to={`/customers/${anum}`}
              onClick={onClose}
              className="text-tremor-brand hover:underline"
            >
              {t.drilldown.fullCustomer}
            </Link>
          </div>
        </div>
      )}
    </Modal>
  );
}
